import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, TextField, Select, Checkbox, RadioGroup, Alert, Spinner, Breadcrumbs, PanelCard, activeDepartments } from '@glplus/glpweb-dsl';
import { useVendor } from '../../lib/CapabilitiesContext';
import {
  VendorApiError,
  vendorApiClient,
  type AssetKind,
  type CreatedProduct,
  type ProductLocationRef,
  type SizeUnit,
} from '../../lib/vendorApiClient';
import {
  SIZE_UNITS,
  checkAddForm,
  emptyForm,
  emptySize,
  ingredientsRequired,
  placeServerProblem,
  type AddFormProblems,
  type AddProductFormState,
  type AttachmentChoice,
  type FamilyField,
  type SizeField,
  type SizeRowForm,
} from '../../lib/addProductForm';
import { notifyProductsChanged } from '../../lib/productEvents';
import { BORDER, GREEN_INK, HEADING, MUTED, RED, FieldLabel, PageTitle, ProductStatusChip, categoryLabel, departmentLabel, describeProductError } from './parts';

/**
 * P5 — Add a product (Figma `fVTA2OnSEz4UOdTeoXeByH` 60:300).
 *
 * What the product is, once; then a row per size, because each size is its own
 * product with its own barcode and prices (§15c). A product with no barcode
 * gets one from us (§10). "I don't have one" is an answer everywhere a photo
 * or barcode is asked for — a blank is not.
 *
 * With `?sizeOf=<product>` the form starts from one of the vendor's own
 * products and the new sizes join its family.
 */

const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;
const MAX_SIZES = 20;
const SIZE_GRID = { xs: '1fr', lg: '0.9fr 1fr 2fr 1.1fr 1fr 1.6fr 32px' };

const NO_PROBLEMS: AddFormProblems = { family: {}, sizes: {} };

export default function AddProduct() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sizeOf = params.get('sizeOf') ?? '';
  const { activeVendorId, has } = useVendor();
  const canEdit = has('catalog:edit');

  const [form, setForm] = useState<AddProductFormState>(emptyForm);
  const [locations, setLocations] = useState<ProductLocationRef[] | null>(null);
  const [baseName, setBaseName] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [problems, setProblems] = useState<AddFormProblems>(NO_PROBLEMS);
  /** A problem the server found, shown at the field it is about. */
  const [serverProblem, setServerProblem] = useState<{ code: string; family?: FamilyField; size?: { key: string; field: SizeField } } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState<'photo' | 'nutritionLabel' | null>(null);
  const [result, setResult] = useState<CreatedProduct[] | null>(null);
  const top = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!activeVendorId) return;
    let alive = true;
    setLoadError(null);
    Promise.all([
      vendorApiClient.listLocations(activeVendorId),
      sizeOf ? vendorApiClient.getProduct(activeVendorId, sizeOf) : Promise.resolve(null),
    ])
      .then(([locs, base]) => {
        if (!alive) return;
        setLocations(locs.items.map((l) => ({ storeId: l.storeId, name: l.name, status: l.status })));
        if (base) {
          const pending = (field: string) => base.changeRequests.find((r) => r.state === 'open' && r.field === field)?.proposedValue ?? '';
          setBaseName(base.info.name);
          setForm((prev) => ({
            ...prev,
            name: base.info.name,
            brand: base.info.brand ?? '',
            department: base.info.department,
            category: base.info.category,
            descriptionShort: base.info.descriptionShort ?? pending('description_short'),
            descriptionLong: base.info.descriptionLong ?? pending('description_long'),
            ingredients: base.info.ingredients ?? '',
            itemNameEs: base.spanish.itemNameEs ?? '',
            descriptionShortEs: base.spanish.descriptionShortEs ?? '',
            sizes: prev.sizes.map((s) => ({ ...s, noBarcode: true })),
          }));
        }
      })
      .catch((e: Error) => alive && setLoadError(describeProductError(e, t)));
    return () => {
      alive = false;
    };
  }, [activeVendorId, sizeOf, t]);

  // In the reader's alphabet, not English's — with "Other" kept last.
  const departments = useMemo(
    () =>
      activeDepartments
        .map((d) => ({ value: d.label, label: departmentLabel(t, d.label), categories: d.categories, last: d.key === 'other' }))
        .sort((a, b) => Number(a.last) - Number(b.last) || a.label.localeCompare(b.label, i18n.language)),
    [t, i18n.language],
  );
  const categories = useMemo(
    () => (departments.find((d) => d.value === form.department)?.categories ?? []).map((c) => ({ value: c.label, label: categoryLabel(i18n.language, form.department, c.label) ?? c.label })),
    [departments, form.department, i18n.language],
  );

  if (loadError) return <Alert severity="error">{loadError}</Alert>;
  if (!activeVendorId || !locations) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }

  const crumbs = (
    <Breadcrumbs
      ariaLabel={t('products.detail.breadcrumbAria')}
      linkComponent={Link}
      items={[
        { label: t('nav.products'), to: '/products' },
        { label: t('products.add.title'), to: '/products/add' },
        { label: t('products.add.form.crumb'), ariaCurrent: true },
      ]}
    />
  );

  if (!canEdit) {
    return (
      <Box>
        {crumbs}
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('products.add.form.noAccess')}
        </Alert>
      </Box>
    );
  }
  if (locations.length === 0) {
    return (
      <Box>
        {crumbs}
        <Alert severity="warning" sx={{ mt: 2 }} data-testid="add-product-no-locations">
          {t('products.errors.no_locations')}{' '}
          <Box component={Link} to="/locations/new" sx={{ color: 'inherit', fontWeight: 700 }}>
            {t('products.detail.prices.addLocation')}
          </Box>
        </Alert>
      </Box>
    );
  }

  if (result) {
    return (
      <AddResult
        crumbs={crumbs}
        name={form.name}
        items={result}
        onAnother={() => {
          setForm(emptyForm());
          setProblems(NO_PROBLEMS);
          setResult(null);
          void navigate('/products/add/new', { replace: true });
        }}
      />
    );
  }

  const set = (patch: Partial<AddProductFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setServerProblem(null);
  };
  const setSize = (key: string, patch: Partial<SizeRowForm>) => {
    setForm((prev) => ({ ...prev, sizes: prev.sizes.map((s) => (s.key === key ? { ...s, ...patch } : s)) }));
    setServerProblem(null);
  };

  const familyMessage = (field: FamilyField): string | undefined => {
    if (serverProblem?.family === field) return t(`products.errors.${serverProblem.code}`);
    const p = problems.family[field];
    return p ? t(`products.errors.${p}`) : undefined;
  };
  const sizeMessage = (key: string, field: SizeField): string | undefined => {
    if (serverProblem?.size?.key === key && serverProblem.size.field === field) return t(`products.errors.${serverProblem.code}`);
    const p = problems.sizes[key]?.[field];
    return p ? t(`products.errors.${p}`) : undefined;
  };

  const upload = async (which: 'photo' | 'nutritionLabel', file: File) => {
    if (file.size > MAX_UPLOAD_BYTES) return setError(t('products.errors.imageTooLarge'));
    setUploading(which);
    setError(null);
    try {
      const kind: AssetKind = which === 'photo' ? 'product-photo' : 'label-photo';
      const { cdnUrl } = await vendorApiClient.uploadAsset(activeVendorId, kind, file);
      set(which === 'photo' ? { photoUrl: cdnUrl } : { nutritionLabelUrl: cdnUrl });
    } catch (e) {
      setError(describeProductError(e, t));
    } finally {
      setUploading(null);
    }
  };

  const submit = async () => {
    const check = checkAddForm(form, sizeOf || undefined);
    setServerProblem(null);
    if (!check.ok) {
      setProblems(check.problems);
      setError(t('products.add.form.fix'));
      top.current?.scrollIntoView({ block: 'start' });
      return;
    }
    setProblems(NO_PROBLEMS);
    setError(null);
    setBusy(true);
    try {
      const { items } = await vendorApiClient.createProducts(activeVendorId, check.input);
      notifyProductsChanged();
      setResult(items);
    } catch (e) {
      const code = e instanceof VendorApiError ? e.code : undefined;
      const placed = placeServerProblem(form, code, e instanceof VendorApiError ? e.sizeIndex : undefined);
      if (code && (placed.family || placed.size)) {
        setServerProblem({ code, ...placed });
        setError(t('products.add.form.fix'));
      } else {
        setError(describeProductError(e, t));
      }
      top.current?.scrollIntoView({ block: 'start' });
    } finally {
      setBusy(false);
    }
  };

  const needIngredients = ingredientsRequired(form);
  const actions = (testId: string) => (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
      <Button ariaLabel={t('common.cancel')} buttonText={t('common.cancel')} variant="text" disabled={busy} onClick={() => void navigate(sizeOf ? `/products/${sizeOf}` : '/products/add')} />
      <Button
        ariaLabel={t('products.add.form.submit')}
        buttonText={t(busy ? 'common.saving' : 'products.add.form.submit')}
        variant="contained"
        color="primary"
        disabled={busy || uploading !== null}
        onClick={() => void submit()}
        dataTestId={testId}
      />
    </Box>
  );

  return (
    <Box ref={top}>
      {crumbs}
      <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 260 }}>
          <PageTitle
            title={baseName ? t('products.add.form.titleSize', { name: baseName }) : t('products.add.form.title')}
            subtitle={t('products.add.form.subtitle')}
          />
        </Box>
        {actions('add-product-submit-top')}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="add-product-error">
          {error}
        </Alert>
      )}

      <PanelCard accent="#343F5B" title={t('products.add.form.about.title')} sx={{ height: 'auto', mt: 3 }}>
        <Typography sx={{ fontSize: 15, color: MUTED }}>{t('products.add.form.about.intro')}</Typography>

        <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2, alignItems: 'start' }}>
          <TextField
            ariaLabel={t('products.add.form.name')}
            label={`${t('products.add.form.name')} *`}
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            required
            error={!!familyMessage('name')}
            errorText={familyMessage('name')}
            disabled={busy}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 200 } }}
            dataTestId="add-product-name"
          />
          <Box data-testid="add-product-department">
            <LabelAbove>{`${t('products.department')} *`}</LabelAbove>
            <Select
              ariaLabel={t('products.department')}
              placeholder={t('products.add.form.choose')}
              value={form.department}
              onChange={(_e, v) => set({ department: v, category: '' })}
              options={departments.map((d) => ({ value: d.value, label: d.label }))}
              required
              disabled={busy}
              size="small"
              fullWidth
            />
            <FieldError text={familyMessage('department')} />
          </Box>
          <Box data-testid="add-product-category">
            <LabelAbove>{`${t('products.add.form.category')} *`}</LabelAbove>
            <Select
              ariaLabel={t('products.add.form.category')}
              placeholder={t('products.add.form.choose')}
              value={form.category}
              onChange={(_e, v) => set({ category: v })}
              options={categories}
              required
              disabled={busy || !form.department}
              size="small"
              fullWidth
            />
            <FieldError text={familyMessage('category')} />
          </Box>
        </Box>

        <Box sx={{ mt: 2, maxWidth: { md: '50%' } }}>
          <TextField
            ariaLabel={t('products.add.form.brand')}
            label={t('products.add.form.brand')}
            value={form.brand}
            onChange={(e) => set({ brand: e.target.value })}
            helperText={t('products.add.form.brandHint')}
            disabled={busy}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 100 } }}
            dataTestId="add-product-brand"
          />
        </Box>
        <Box sx={{ mt: 2 }}>
          <TextField
            ariaLabel={t('products.request.field.description_short')}
            label={`${t('products.request.field.description_short')} *`}
            value={form.descriptionShort}
            onChange={(e) => set({ descriptionShort: e.target.value })}
            required
            error={!!familyMessage('descriptionShort')}
            errorText={familyMessage('descriptionShort')}
            helperText={t('products.add.form.descriptionHint')}
            disabled={busy}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 500 } }}
            dataTestId="add-product-description"
          />
        </Box>
        <Box sx={{ mt: 2 }}>
          <TextField
            ariaLabel={t('products.add.form.descriptionLong')}
            label={t('products.add.form.descriptionLong')}
            placeholder={t('products.add.form.descriptionLongPlaceholder')}
            value={form.descriptionLong}
            onChange={(e) => set({ descriptionLong: e.target.value })}
            multiline
            minRows={2}
            disabled={busy}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 5000 } }}
            dataTestId="add-product-description-long"
          />
        </Box>
        <Box sx={{ mt: 2 }}>
          <TextField
            ariaLabel={t('products.request.field.ingredients')}
            label={needIngredients ? `${t('products.request.field.ingredients')} *` : t('products.add.form.ingredientsOptional')}
            value={form.ingredients}
            onChange={(e) => set({ ingredients: e.target.value })}
            required={needIngredients}
            error={!!familyMessage('ingredients')}
            errorText={familyMessage('ingredients')}
            helperText={t('products.add.form.ingredientsHint')}
            multiline
            minRows={2}
            disabled={busy}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 5000 } }}
            dataTestId="add-product-ingredients"
          />
        </Box>

        <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <AttachmentQuestion
            which="photo"
            choice={form.photo}
            url={form.photoUrl}
            uploading={uploading === 'photo'}
            disabled={busy}
            problem={familyMessage('photo')}
            onChoice={(photo) => set({ photo })}
            onFile={(f) => void upload('photo', f)}
          />
          <AttachmentQuestion
            which="nutritionLabel"
            choice={form.nutritionLabel}
            url={form.nutritionLabelUrl}
            uploading={uploading === 'nutritionLabel'}
            disabled={busy}
            problem={familyMessage('nutritionLabel')}
            onChoice={(nutritionLabel) => set({ nutritionLabel })}
            onFile={(f) => void upload('nutritionLabel', f)}
          />
        </Box>

        <Box sx={{ mt: 2.5, maxWidth: 320 }}>
          <TextField
            ariaLabel={t('products.add.form.servings')}
            label={t('products.add.form.servings')}
            value={form.servings}
            onChange={(e) => set({ servings: e.target.value })}
            error={!!familyMessage('servings')}
            errorText={familyMessage('servings')}
            disabled={busy}
            fullWidth
            slotProps={{ htmlInput: { inputMode: 'decimal', maxLength: 7 } }}
            dataTestId="add-product-servings"
          />
        </Box>
      </PanelCard>

      <PanelCard accent="#F67A1A" title={t('products.add.form.sizes.title')} sx={{ height: 'auto', mt: 3 }}>
        <Typography sx={{ fontSize: 15, color: MUTED }}>{t('products.add.form.sizes.intro')}</Typography>
        <Box sx={{ mt: 2, display: { xs: 'none', lg: 'grid' }, gridTemplateColumns: SIZE_GRID, gap: 2 }}>
          {(['size', 'unit', 'barcode', 'sku', 'price', 'locations'] as const).map((c) => (
            <FieldLabel key={c}>{t(`products.add.form.sizes.col.${c}`)}</FieldLabel>
          ))}
          <span />
        </Box>
        {form.sizes.map((row, index) => (
          <SizeRow
            key={row.key}
            row={row}
            number={index + 1}
            locations={locations}
            disabled={busy}
            removable={form.sizes.length > 1}
            message={(field) => sizeMessage(row.key, field)}
            onChange={(patch) => setSize(row.key, patch)}
            onRemove={() => {
              setForm((prev) => ({ ...prev, sizes: prev.sizes.filter((s) => s.key !== row.key) }));
              setServerProblem(null);
            }}
          />
        ))}
        {form.sizes.length < MAX_SIZES && (
          <Box sx={{ mt: 1.5 }}>
            <Button
              ariaLabel={t('products.add.form.sizes.another')}
              buttonText={t('products.add.form.sizes.another')}
              variant="text"
              startIcon="add"
              disabled={busy}
              onClick={() => {
                const last = form.sizes[form.sizes.length - 1];
                // The next size of the same thing is nearly always the same unit, sold at the same places.
                const next = { ...emptySize(last?.sizeUnit), noBarcode: last?.noBarcode ?? false, allLocations: last?.allLocations ?? true, storeIds: last?.storeIds ?? [] };
                setForm((prev) => ({ ...prev, sizes: [...prev.sizes, next] }));
              }}
              dataTestId="add-product-another-size"
            />
          </Box>
        )}
      </PanelCard>

      <PanelCard accent="#343F5B" title={t('products.add.form.spanish.title')} sx={{ height: 'auto', mt: 3 }}>
        <Typography sx={{ fontSize: 15, color: MUTED }}>{t('products.add.form.spanish.intro')}</Typography>
        <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <TextField
            ariaLabel={t('products.detail.spanish.itemNameEs')}
            label={t('products.detail.spanish.itemNameEs')}
            value={form.itemNameEs}
            onChange={(e) => set({ itemNameEs: e.target.value })}
            disabled={busy}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 200, lang: 'es' } }}
            dataTestId="add-product-name-es"
          />
          <TextField
            ariaLabel={t('products.detail.spanish.descriptionShortEs')}
            label={t('products.detail.spanish.descriptionShortEs')}
            value={form.descriptionShortEs}
            onChange={(e) => set({ descriptionShortEs: e.target.value })}
            disabled={busy}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 500, lang: 'es' } }}
            dataTestId="add-product-description-es"
          />
        </Box>
      </PanelCard>

      <Alert severity="info" sx={{ mt: 3 }}>
        {t('products.add.form.note')}
      </Alert>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>{actions('add-product-submit')}</Box>
    </Box>
  );
}

/** A Select's label, placed where a text field puts its own so the two line up in one row. */
function LabelAbove({ children }: { children: string }) {
  return (
    <Typography aria-hidden sx={{ fontSize: 13, lineHeight: '20px', color: HEADING, mt: '12px', mb: '4px', pl: '4px' }}>
      {children}
    </Typography>
  );
}

function FieldError({ text }: { text: string | undefined }) {
  if (!text) return null;
  return (
    <Typography role="alert" sx={{ mt: 0.5, fontSize: 13, fontWeight: 700, color: RED }}>
      {text}
    </Typography>
  );
}

/** "Upload one" or "I don't have one" — both are answers; leaving it blank is not (§15c). */
function AttachmentQuestion({
  which,
  choice,
  url,
  uploading,
  disabled,
  problem,
  onChoice,
  onFile,
}: {
  which: 'photo' | 'nutritionLabel';
  choice: AttachmentChoice;
  url: string | null;
  uploading: boolean;
  disabled: boolean;
  problem: string | undefined;
  onChoice: (choice: AttachmentChoice) => void;
  onFile: (file: File) => void;
}) {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);
  const base = `products.add.form.${which}`;
  return (
    <Box data-testid={`add-product-${which}`}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: HEADING }}>
        {t(`${base}.label`)} <Box component="span" aria-hidden sx={{ color: RED }}>*</Box>
      </Typography>
      <RadioGroup
        ariaLabel={t(`${base}.label`)}
        name={`add-product-${which}`}
        row
        value={choice}
        onChange={(e) => onChoice(e.target.value as AttachmentChoice)}
        options={[
          { value: 'upload', label: t(`${base}.upload`), disabled },
          { value: 'none', label: t(`${base}.none`), disabled },
        ]}
      />
      {choice === 'none' && <Typography sx={{ fontSize: 13, color: GREEN_INK }}>{t(`${base}.noneHint`)}</Typography>}
      {choice === 'upload' && (
        <Box sx={{ mt: 1, p: 2, border: `1px dashed #9AA3AD`, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {url && <Box component="img" src={url} alt={t('products.request.attach.previewAlt')} sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: '8px' }} />}
          <Typography sx={{ flex: 1, minWidth: 160, fontSize: 13, color: MUTED }}>{t(url ? `${base}.uploaded` : 'products.request.attach.hint')}</Typography>
          {uploading ? (
            <Spinner color="brand" />
          ) : (
            <>
              <input
                ref={fileInput}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                data-testid={`add-product-${which}-file`}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onFile(f);
                  e.target.value = '';
                }}
              />
              <Button
                ariaLabel={t(url ? 'products.request.attach.replace' : 'products.request.attach.choose')}
                buttonText={t(url ? 'products.request.attach.replace' : 'products.request.attach.choose')}
                variant="outlinedGreen"
                disabled={disabled}
                onClick={() => fileInput.current?.click()}
              />
            </>
          )}
        </Box>
      )}
      <FieldError text={problem} />
    </Box>
  );
}

function SizeRow({
  row,
  number,
  locations,
  disabled,
  removable,
  message,
  onChange,
  onRemove,
}: {
  row: SizeRowForm;
  number: number;
  locations: ProductLocationRef[];
  disabled: boolean;
  removable: boolean;
  message: (field: SizeField) => string | undefined;
  onChange: (patch: Partial<SizeRowForm>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const n = { number };
  return (
    <Box
      role="group"
      aria-label={t('products.add.form.sizes.rowAria', n)}
      data-testid={`add-product-size-${number}`}
      sx={{ py: 1.75, borderBottom: `1px solid ${BORDER}`, '&:last-of-type': { borderBottom: 'none' } }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: SIZE_GRID, gap: 2, alignItems: 'start' }}>
        <TextField
          ariaLabel={t('products.add.form.sizes.sizeAria', n)}
          placeholder="1"
          value={row.sizeValue}
          onChange={(e) => onChange({ sizeValue: e.target.value })}
          error={!!message('size')}
          disabled={disabled}
          size="small"
          fullWidth
          slotProps={{ htmlInput: { inputMode: 'decimal', maxLength: 10 } }}
          dataTestId={`add-product-size-value-${number}`}
        />
        <Box data-testid={`add-product-size-unit-${number}`}>
          <Select
            ariaLabel={t('products.add.form.sizes.unitAria', n)}
            value={row.sizeUnit}
            onChange={(_e, v) => onChange({ sizeUnit: v as SizeUnit })}
            options={SIZE_UNITS.map((u) => ({ value: u, label: t(`products.add.form.units.${u.replace(' ', '_')}`) }))}
            disabled={disabled}
            size="small"
            fullWidth
          />
        </Box>
        <Box>
          <Box data-testid={`add-product-size-nobarcode-${number}`}>
            <Checkbox
              ariaLabel={t('products.add.form.sizes.noBarcodeAria', n)}
              label={t('products.add.form.sizes.noBarcode')}
              checked={row.noBarcode}
              disabled={disabled}
              onChange={(e) => onChange({ noBarcode: e.target.checked })}
              size="small"
            />
          </Box>
          {row.noBarcode ? (
            <Typography sx={{ fontSize: 13, color: GREEN_INK }}>{t('products.add.form.sizes.weAssign')}</Typography>
          ) : (
            <TextField
              ariaLabel={t('products.add.form.sizes.barcodeAria', n)}
              placeholder={t('products.add.form.sizes.barcodePlaceholder')}
              value={row.barcode}
              onChange={(e) => onChange({ barcode: e.target.value })}
              error={!!message('barcode')}
              errorText={message('barcode')}
              disabled={disabled}
              size="small"
              fullWidth
              slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 20 } }}
              dataTestId={`add-product-size-barcode-${number}`}
            />
          )}
        </Box>
        <TextField
          ariaLabel={t('products.add.form.sizes.skuAria', n)}
          placeholder={t('products.add.form.sizes.skuPlaceholder')}
          value={row.vendorSku}
          onChange={(e) => onChange({ vendorSku: e.target.value })}
          disabled={disabled}
          size="small"
          fullWidth
          slotProps={{ htmlInput: { maxLength: 64 } }}
          dataTestId={`add-product-size-sku-${number}`}
        />
        <TextField
          ariaLabel={t('products.add.form.sizes.priceAria', n)}
          placeholder="5.99"
          value={row.price}
          onChange={(e) => onChange({ price: e.target.value })}
          error={!!message('price')}
          disabled={disabled}
          size="small"
          fullWidth
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          dataTestId={`add-product-size-price-${number}`}
        />
        <Box data-testid={`add-product-size-locations-${number}`}>
          <Select
            ariaLabel={t('products.add.form.sizes.locationsAria', n)}
            value={row.allLocations ? 'all' : 'some'}
            onChange={(_e, v) => onChange({ allLocations: v === 'all' })}
            options={[
              { value: 'all', label: t('products.add.form.sizes.allLocations', { count: locations.length }) },
              ...(locations.length > 1 ? [{ value: 'some', label: t('products.add.form.sizes.someLocations') }] : []),
            ]}
            disabled={disabled}
            size="small"
            fullWidth
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
          {removable && (
            <Button
              ariaLabel={t('products.add.form.sizes.removeAria', n)}
              variant="iconButton"
              startIcon="delete"
              disabled={disabled}
              onClick={onRemove}
              dataTestId={`add-product-size-remove-${number}`}
            />
          )}
        </Box>
      </Box>
      {(message('size') || message('price')) && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }} data-testid={`add-product-size-problems-${number}`}>
          <FieldError text={message('size')} />
          <FieldError text={message('price')} />
        </Box>
      )}
      {!row.allLocations && (
        <Box role="group" aria-label={t('products.add.form.sizes.locationsAria', n)} sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {locations.map((l) => (
            <Box key={l.storeId} data-testid={`add-product-size-${number}-location-${l.storeId}`}>
              <Checkbox
                ariaLabel={l.name}
                label={l.name}
                checked={row.storeIds.includes(l.storeId)}
                disabled={disabled}
                onChange={(e) =>
                  onChange({ storeIds: e.target.checked ? [...row.storeIds, l.storeId] : row.storeIds.filter((id) => id !== l.storeId) })
                }
                size="small"
              />
            </Box>
          ))}
          <FieldError text={message('locations')} />
        </Box>
      )}
    </Box>
  );
}

/** What happened to each size: live, waiting on a question, or already theirs. */
function AddResult({
  crumbs,
  name,
  items,
  onAnother,
}: {
  crumbs: React.ReactNode;
  name: string;
  items: CreatedProduct[];
  onAnother: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const waiting = items.filter((i) => i.status === 'needs_answer').length;
  const added = items.filter((i) => i.status === 'added').length;
  return (
    <Box data-testid="add-product-result">
      {crumbs}
      <Box sx={{ mt: 2 }}>
        <PageTitle
          title={t(waiting > 0 && added === 0 ? 'products.add.result.titleWaiting' : 'products.add.result.title', { name })}
          subtitle={waiting > 0 ? t('products.add.result.subtitleWaiting', { count: waiting }) : t('products.add.result.subtitle')}
        />
      </Box>
      <Box sx={{ mt: 3, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden' }}>
        {items.map((item) => (
          <Box
            key={item.vendorProductId}
            data-testid={`add-product-result-${item.status}`}
            sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', px: 2.5, py: 2, borderBottom: `1px solid ${BORDER}`, '&:last-of-type': { borderBottom: 'none' } }}
          >
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: HEADING }}>
                {name} · {item.sizeValue} {t(`products.add.form.units.${item.sizeUnit.replace(' ', '_')}`, { defaultValue: item.sizeUnit })}
              </Typography>
              <Typography sx={{ fontSize: 13, color: MUTED }}>
                {item.status === 'already_yours'
                  ? t('products.add.result.alreadyYours')
                  : item.status === 'needs_answer'
                    ? t('products.add.result.needsAnswer')
                    : item.assignedBarcode
                      ? t('products.add.result.addedAssigned', { barcode: item.assignedBarcode })
                      : t('products.add.result.added')}
              </Typography>
            </Box>
            {item.status === 'needs_answer' && <ProductStatusChip status="needs_answer" />}
            <Button
              ariaLabel={t(item.status === 'needs_answer' ? 'products.add.result.answer' : 'products.detail.sizes.open')}
              buttonText={t(item.status === 'needs_answer' ? 'products.add.result.answer' : 'products.detail.sizes.open')}
              variant={item.status === 'needs_answer' ? 'contained' : 'outlinedGreen'}
              color={item.status === 'needs_answer' ? 'primary' : undefined}
              onClick={() => void navigate(`/products/${item.vendorProductId}`)}
              dataTestId={`add-product-result-open-${item.vendorProductId}`}
            />
          </Box>
        ))}
      </Box>
      {added > 0 && <Typography sx={{ mt: 1.5, fontSize: 13, color: MUTED }}>{t('products.add.result.reviewNote')}</Typography>}
      <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Button ariaLabel={t('products.add.result.another')} buttonText={t('products.add.result.another')} variant="contained" color="primary" onClick={onAnother} dataTestId="add-product-another" />
        <Button ariaLabel={t('products.add.result.back')} buttonText={t('products.add.result.back')} variant="outlinedGreen" onClick={() => void navigate('/products')} />
      </Box>
    </Box>
  );
}
